// src/components/EmailSummaryModal.js
import React, { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functionsClient } from "../firebase";
import {
  getPostSessionAutomaticEmailLabel,
  getPostSessionFinalizationLabel,
  getPostSessionManualResendEligibility,
  getPostSessionManualResendLabel,
  getPostSessionReminderLabel,
  normalizePostSessionContinuation,
} from "../utils/summaryDelivery";

function resolveSessionId(value = "") {
  const explicit = String(value || "").trim();
  if (explicit) return explicit;
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("id") || "";
}

function canonicalSummaryText(value = "") {
  const text = String(value || "").trim();
  if (!/^Your session summary:\s*/i.test(text)) return text;
  return text
    .replace(/^Your session summary:\s*/i, "")
    .split(/\n\s*\nThemes:/i)[0]
    .trim();
}

function continuationFromProps({
  sessionRecord,
  postSessionContinuation,
  initialDelivery,
  draftSummary,
  summaryGenerationStatus,
  summaryPersistenceStatus,
}) {
  return normalizePostSessionContinuation({
    ...(sessionRecord && typeof sessionRecord === "object"
      ? sessionRecord
      : {}),
    ...(postSessionContinuation && typeof postSessionContinuation === "object"
      ? { postSessionContinuation }
      : {}),
    sessionSummary: {
      ...(
        sessionRecord?.sessionSummary &&
        typeof sessionRecord.sessionSummary === "object"
          ? sessionRecord.sessionSummary
          : {}
      ),
      summaryText: String(draftSummary || "").trim(),
      generationStatus: summaryGenerationStatus,
      status: summaryGenerationStatus,
      persistenceStatus: summaryPersistenceStatus,
    },
    summaryDelivery: initialDelivery,
  });
}

function continuationFromCallablePayload(
  payload = {},
  previous = {},
  sessionRecord = {}
) {
  const trusted =
    payload?.postSessionContinuation ||
    payload?.continuation ||
    payload?.session?.postSessionContinuation;
  if (trusted && typeof trusted === "object") {
    return normalizePostSessionContinuation({
      ...(sessionRecord && typeof sessionRecord === "object"
        ? sessionRecord
        : {}),
      postSessionContinuation: trusted,
    });
  }

  const manualResend =
    payload?.manualResend ||
    payload?.manualResendAttempt ||
    payload?.attempt;
  if (!manualResend || typeof manualResend !== "object") return previous;
  const previousAttempts = Array.isArray(previous?.manualResend?.attempts)
    ? previous.manualResend.attempts
    : [];
  const nextAttempts = Array.isArray(manualResend.attempts)
    ? manualResend.attempts
    : [...previousAttempts, manualResend];
  const existingContinuation =
    sessionRecord?.postSessionContinuation &&
    typeof sessionRecord.postSessionContinuation === "object"
      ? sessionRecord.postSessionContinuation
      : {};
  return normalizePostSessionContinuation({
    ...(sessionRecord && typeof sessionRecord === "object"
      ? sessionRecord
      : {}),
    postSessionContinuation: {
      ...existingContinuation,
      manualResend: {
        ...(existingContinuation.manualResend || {}),
        ...manualResend,
        attempts: nextAttempts,
      },
      return: existingContinuation.return || previous.return,
    },
  });
}

export default function EmailSummaryModal({
  open,
  onClose,
  draftSummary = "",
  orgId = "org_default",
  sessionId = "",
  summaryGenerationStatus = "",
  summaryPersistenceStatus = "",
  initialDelivery = null,
  postSessionContinuation = null,
  sessionRecord = null,
}) {
  const canonicalSummary = canonicalSummaryText(draftSummary);
  const initialContinuation = useMemo(() => continuationFromProps({
    sessionRecord,
    postSessionContinuation,
    initialDelivery,
    draftSummary: canonicalSummary,
    summaryGenerationStatus,
    summaryPersistenceStatus,
  }), [
    postSessionContinuation,
    initialDelivery,
    canonicalSummary,
    summaryGenerationStatus,
    summaryPersistenceStatus,
    sessionRecord,
  ]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [continuation, setContinuation] = useState(initialContinuation);

  useEffect(() => {
    if (!open) return;
    setSending(false);
    setError("");
    setNotice("");
    setContinuation(initialContinuation);
  }, [open, initialContinuation]);

  if (!open) return null;

  const resolvedSessionId = resolveSessionId(sessionId);
  const safeOrgId = String(orgId || "").trim();
  const persistenceStatus = String(summaryPersistenceStatus || "").trim().toLowerCase();
  const summaryReady = Boolean(
    canonicalSummary &&
    continuation.finalization.status === "ready" &&
    continuation.provenance?.trustedFinalSummary === true &&
    !["failed", "pending"].includes(persistenceStatus)
  );
  const manualResendEligibility = getPostSessionManualResendEligibility(
    continuation
  );
  const provenanceBoundary = continuation.provenance?.boundary || {};
  const targetMatchesProvenance = Boolean(
    String(provenanceBoundary.orgId || "") === safeOrgId &&
    String(provenanceBoundary.sessionId || "") === resolvedSessionId
  );
  const canRequestManualResend = Boolean(
    safeOrgId &&
    resolvedSessionId &&
    summaryReady &&
    targetMatchesProvenance &&
    manualResendEligibility.eligible
  );

  const requestManualResend = async () => {
    if (!canRequestManualResend || sending) {
      if (!safeOrgId || !resolvedSessionId) {
        setError("The organisation and authoritative session ID are required.");
      } else if (!summaryReady) {
        setError("A current final canonical summary must be ready before a manual resend.");
      } else if (!targetMatchesProvenance) {
        setError("The resend target does not match the authoritative session provenance.");
      } else if (!manualResendEligibility.eligible) {
        setError(
          continuation.automaticEmail.status === "queued"
            ? "The automatic summary email is already queued and does not need a manual resend."
            : continuation.automaticEmail.status === "sent"
              ? "The automatic summary email was already accepted and does not need a manual resend."
              : "Wait for the automatic summary-email attempt to finish before requesting a resend."
        );
      }
      return;
    }

    setSending(true);
    setError("");
    setNotice("");
    try {
      const sendSessionSummaryEmail = httpsCallable(
        functionsClient,
        "sendSessionSummaryEmail"
      );
      const result = await sendSessionSummaryEmail({
        orgId: safeOrgId,
        sessionId: resolvedSessionId,
        requestId:
          (
            typeof window !== "undefined" &&
            typeof window.crypto?.randomUUID === "function"
              ? window.crypto.randomUUID()
              : ""
          ) ||
          `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });
      const payload = result?.data || {};
      setContinuation((previous) => continuationFromCallablePayload(
        payload,
        previous,
        sessionRecord || {}
      ));
      if (payload?.ok === false) {
        throw new Error(
          String(payload?.reason || payload?.error || "Manual resend failed.")
        );
      }
      const responseAttempt =
        payload?.attempt ||
        payload?.manualResendAttempt ||
        payload?.manualResend?.attempts?.[
          Math.max(0, (payload?.manualResend?.attempts?.length || 1) - 1)
        ] ||
        {};
      const responseStatus = String(
        payload?.status || responseAttempt?.status || ""
      ).toLowerCase();
      const accepted = Boolean(
        payload?.accepted === true ||
        ["queue_accepted", "provider_accepted", "delivered"].includes(responseStatus)
      );
      if (
        payload?.accepted === false ||
        ["failed", "ambiguous", "bounced"].includes(responseStatus)
      ) {
        setError(
          responseStatus === "ambiguous"
            ? "The server recorded the attempt, but its acceptance status is unverified."
            : "The server recorded the attempt, but the manual resend failed."
        );
      } else {
        setNotice(
          accepted
            ? "The server recorded the manual resend request. Delivery is unconfirmed unless the status below says otherwise."
            : "The server recorded the manual resend attempt. Its acceptance status is shown below."
        );
      }
    } catch (requestError) {
      setError(
        String(
          requestError?.message ||
          "The manual resend request could not be completed."
        )
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={backdrop} onClick={() => onClose(false)} role="presentation">
      <div
        style={card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-summary-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="email-summary-title" style={{ margin: 0 }}>
          Resend session summary
        </h3>
        <p style={{ marginTop: 6, color: "var(--muted)" }}>
          This is a manual resend or recovery control. The normal first summary
          email is handled automatically after finalisation.
        </p>

        <div style={auditBox}>
          <div>
            <strong>Canonical summary:</strong>{" "}
            {getPostSessionFinalizationLabel(continuation)}
          </div>
          <div>
            <strong>Automatic email:</strong>{" "}
            {getPostSessionAutomaticEmailLabel(continuation)}
          </div>
          <div>
            <strong>Manual resend:</strong>{" "}
            {getPostSessionManualResendLabel(continuation)}
          </div>
          <div>
            <strong>Reminder:</strong>{" "}
            {getPostSessionReminderLabel(continuation)}
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {summaryReady
              ? "Current canonical summary"
              : "Summary preview — provenance unverified"}
          </div>
          <div style={summaryBox} data-testid="canonical-summary">
            {canonicalSummary || "No current canonical summary is available."}
          </div>
          <div style={{ ...mutedDetail, marginTop: 6 }}>
            The recipient, summary version, and secure return context are
            resolved from the authoritative server-side session record.
          </div>
        </div>

        {notice ? <div style={noticeBox} role="status">{notice}</div> : null}
        {error ? <div style={errorBox} role="alert">{error}</div> : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => onClose(false)}
            disabled={sending}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={requestManualResend}
            disabled={sending || !canRequestManualResend}
          >
            {sending
              ? "Requesting…"
              : continuation.manualResend.latestAttempt?.status === "failed"
                ? "Retry manual resend"
                : "Send manual resend"}
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop = {
  position: "fixed",
  inset: 0,
  display: "grid",
  placeItems: "center",
  background: "var(--color-overlay)",
  zIndex: 1000,
};

const card = {
  width: "min(620px, 92vw)",
  background: "var(--card)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: 14,
  padding: 18,
  boxShadow: "var(--shadow-lg)",
};

const auditBox = {
  display: "grid",
  gap: 5,
  marginTop: 10,
  padding: "10px 12px",
  border: "1px solid var(--border)",
  borderRadius: 10,
  background: "color-mix(in srgb, var(--ui-surface) 94%, var(--color-background) 6%)",
};

const summaryBox = {
  whiteSpace: "pre-wrap",
  maxHeight: "240px",
  overflowY: "auto",
  padding: "10px 12px",
  border: "1px solid var(--border)",
  borderRadius: 10,
  background: "var(--ui-surface)",
};

const mutedDetail = {
  color: "var(--muted)",
  fontSize: 13,
};

const noticeBox = {
  marginTop: 12,
  padding: "8px 10px",
  border: "1px solid color-mix(in srgb, var(--color-success) 28%, var(--ui-border))",
  borderRadius: 8,
  color: "var(--ui-text)",
};

const errorBox = {
  marginTop: 12,
  background: "color-mix(in srgb, var(--color-danger) 8%, var(--ui-surface))",
  border: "1px solid color-mix(in srgb, var(--color-danger) 28%, var(--ui-border))",
  padding: "8px 10px",
  borderRadius: 8,
  color: "var(--ui-text)",
};
